import Button from '@asphalt-react/button';
import Dropdown from '@asphalt-react/dropdown';
import TextField from '@asphalt-react/textfield';
import { Cross, Tick, Trash } from '@asphalt-react/iconpack';
import { IconButton } from '@asphalt-react/button';
import React, { useContext, useEffect, useState } from 'react';
import LoadingPage from '../../common/pages/LoadingPage';
import Permission from '../../authorization/components/Permission';
import actions from '../../authorization/models/actions';
import { UserContext } from '../../common/hooks/UserContext';
import { getAppIds, addAppId, deleteAppId } from '../api/appIdsApi';
import styles from './AppIdsPage.module.css';

const texts = {
    heading: 'App IDs',
    addButton: 'Add App ID',
    appIdPlaceholder: 'Enter App ID',
    addFailedMessage: 'Could not add the App ID. Please try again.',
    duplicateMessage: 'This App ID already exists.',
    deleteFailedMessage: 'Could not remove the App ID. Please try again.',
};

// Which app_ids Firestore docs each Coffer build may show / edit.
// Staging → Integration + QA; Production → Integration + QA + Prod.
const ALLOWED_APP_ID_ENVS = {
    integration: ['integration', 'qa'],
    prod: ['integration', 'qa', 'prod'],
};

function filterAppIdsForCofferEnv(allAppIds) {
    const cofferEnv = process.env.REACT_APP_ENVIRONMENT;
    const allowed = ALLOWED_APP_ID_ENVS[cofferEnv] || ALLOWED_APP_ID_ENVS.prod;
    const filtered = {};
    allowed.forEach((env) => {
        if (env === 'prod') {
            // Prefer "prod"; accept legacy "production" doc id if present.
            filtered.prod = allAppIds.prod || allAppIds.production || [];
        } else {
            filtered[env] = allAppIds[env] || [];
        }
    });
    return filtered;
}

function toEnvItems(appIds) {
    return Object.keys(appIds).map((env, idx) => ({
        id: String(idx),
        key: env.charAt(0).toUpperCase() + env.slice(1),
        identifier: env,
    }));
}

function AppIdsPage() {
    const { user } = useContext(UserContext);

    const [appIds, setAppIds] = useState({});
    const [isLoading, setIsLoading] = useState(true);

    const [isAddRowVisible, setIsAddRowVisible] = useState(false);
    const [newAppId, setNewAppId] = useState('');
    const [selectedEnvItem, setSelectedEnvItem] = useState(null);
    const [isAdding, setIsAdding] = useState(false);
    const [addError, setAddError] = useState(null);

    const [deletingKey, setDeletingKey] = useState(null);

    useEffect(() => {
        getAppIds()
            .then((ids) => {
                const filtered = filterAppIdsForCofferEnv(ids);
                setAppIds(filtered);
                const items = toEnvItems(filtered);
                if (items.length > 0) setSelectedEnvItem(items[0]);
                setIsLoading(false);
            })
            .catch((error) => {
                console.error(error);
                setIsLoading(false);
            });
    }, []);

    const envItems = toEnvItems(appIds);

    const handleAddAppId = async () => {
        const trimmed = newAppId.trim();
        if (!trimmed || !selectedEnvItem) return;
        const env = selectedEnvItem.identifier;
        if ((appIds[env] || []).includes(trimmed)) {
            setAddError(texts.duplicateMessage);
            return;
        }
        setIsAdding(true);
        setAddError(null);
        try {
            await addAppId(trimmed, env, user.uid);
            setAppIds((prev) => ({
                ...prev,
                [env]: [...(prev[env] || []), trimmed],
            }));
            setNewAppId('');
            setIsAddRowVisible(false);
        } catch (error) {
            console.error(error);
            setAddError(
                error.message === 'App ID already exists'
                    ? texts.duplicateMessage
                    : texts.addFailedMessage
            );
        } finally {
            setIsAdding(false);
        }
    };

    const handleDeleteAppId = async (appId, env) => {
        const key = `${env}:${appId}`;
        setDeletingKey(key);
        try {
            await deleteAppId(appId, env);
            setAppIds((prev) => ({
                ...prev,
                [env]: prev[env].filter((id) => id !== appId),
            }));
        } catch (error) {
            console.error(error);
            alert(texts.deleteFailedMessage);
        } finally {
            setDeletingKey(null);
        }
    };

    return (
        <React.Fragment>
            {isLoading ? (
                <LoadingPage />
            ) : (
                <div className={styles.appIdsPageContainer}>
                    <div className={styles.headingAndButtonContainer}>
                        <h3 className={styles.heading}>{texts.heading}</h3>
                        <div className={styles.buttonContainer}>
                            <Permission action={actions.ADD_APP_ID}>
                                <Button
                                    onClick={() => setIsAddRowVisible(true)}>
                                    {texts.addButton}
                                </Button>
                            </Permission>
                        </div>
                    </div>
                    {isAddRowVisible && (
                        <React.Fragment>
                            <div className={styles.addRowContainer}>
                                <Dropdown
                                    items={envItems}
                                    initialSelectedItem={selectedEnvItem}
                                    onChange={(item) => {
                                        setSelectedEnvItem(item);
                                        setAddError(null);
                                    }}
                                />
                                <TextField
                                    size='m'
                                    stretch
                                    type='text'
                                    placeholder={texts.appIdPlaceholder}
                                    value={newAppId}
                                    onChange={(e) => {
                                        setNewAppId(e.target.value);
                                        setAddError(null);
                                    }}
                                />
                                <Button
                                    nude
                                    qualifier={<Tick />}
                                    disabled={isAdding}
                                    onClick={handleAddAppId}
                                />
                                <Button
                                    nude
                                    qualifier={<Cross />}
                                    disabled={isAdding}
                                    onClick={() => {
                                        setNewAppId('');
                                        setAddError(null);
                                        setIsAddRowVisible(false);
                                    }}
                                />
                            </div>
                            {addError && (
                                <p className={styles.addError}>{addError}</p>
                            )}
                        </React.Fragment>
                    )}
                    {Object.entries(appIds).map(([env, ids]) => (
                        <div key={env} className={styles.envSection}>
                            <h4 className={styles.envHeading}>
                                {env.charAt(0).toUpperCase() + env.slice(1)}
                            </h4>
                            <div className={styles.appIdListContainer}>
                                {ids.map((appId) => (
                                    <div key={appId} className={styles.appIdItemContainer}>
                                        <p className={styles.bodyMediumText}>{appId}</p>
                                        <Permission action={actions.ADD_APP_ID}>
                                            <IconButton
                                                nude
                                                icon={<Trash />}
                                                disabled={deletingKey === `${env}:${appId}`}
                                                onClick={() => handleDeleteAppId(appId, env)}
                                                className={styles.deleteButton}
                                            />
                                        </Permission>
                                    </div>
                                ))}
                                {ids.length === 0 && (
                                    <p className={styles.emptyText}>No App IDs added yet.</p>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </React.Fragment>
    );
}

export default AppIdsPage;
